import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { style } from './price.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { fontStyles, fontVariants, fontVariantsType } from 'elements/_shared/fonts.style.js'

export const priceVariants = ['default', 'sale'] as const
export const priceSizes = ['l', 'm', 's'] as const
export const priceAsOptions = ['span', 'p'] as const
type SegmentToken = 'lead' | 'value' | 'uom' | 'vat'
type RowValues = { lead: string, uom: string, vat: string }

export const priceLayouts = [
  'inline',
  'inline-vat-below',
  'lead-inline',
  'lead-inline-vat-below',
  'inline-meta-below',
  'stacked',
] as const

type LayoutSegments = Record<'top' | 'middle' | 'bottom', SegmentToken[]>

const layoutDefinitions: Record<priceLayoutTypes[number], LayoutSegments> = {
  inline: {
    top: [],
    middle: ['lead', 'value', 'uom', 'vat'],
    bottom: [],
  },
  'inline-vat-below': {
    top: [],
    middle: ['lead', 'value', 'uom'],
    bottom: ['vat'],
  },
  'lead-inline': {
    top: ['lead'],
    middle: ['value', 'uom', 'vat'],
    bottom: [],
  },
  'lead-inline-vat-below': {
    top: ['lead'],
    middle: ['value', 'uom'],
    bottom: ['vat'],
  },
  'inline-meta-below': {
    top: [],
    middle: ['lead', 'value'],
    bottom: ['uom', 'vat'],
  },
  stacked: {
    top: ['lead'],
    middle: ['value'],
    bottom: ['uom', 'vat'],
  },
}

export type priceVariantTypes = typeof priceVariants
export type priceSizeTypes = typeof priceSizes
export type priceAsOptionsType = typeof priceAsOptions
export type priceLayoutTypes = typeof priceLayouts

@customElement('cse-price')
export class CSEPrice extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, fontStyles, style]

  @property({ type: String })
  as: priceAsOptionsType[number] = 'p'

  @property({ type: String })
  variant: priceVariantTypes[number] = 'default'

  @property({ type: String })
  size: priceSizeTypes[number] = 'm'

  @property({ type: String })
  layout: priceLayoutTypes[number] = 'inline'

  @property({ type: String })
  lead = ''

  @property({ type: String })
  uom = ''

  @property({ type: String })
  vat = ''

  @property({ type: String })
  typography: fontVariantsType[number] = 'body'

  override render() {
    const layoutDefinition = layoutDefinitions[this.layout as priceLayoutTypes[number]]
    const layoutName = layoutDefinition ? this.layout : 'inline'
    const layout = layoutDefinition ?? layoutDefinitions.inline
    const typography = this.resolveTypography()
    const rowValues: RowValues = { lead: this.lead, uom: this.uom, vat: this.vat }
    const hasTopRow = this.rowHasContent(layout.top, rowValues)
    const hasBottomRow = this.rowHasContent(layout.bottom, rowValues)
    const classes = classMap({
      'cse-price': true,
      [`variant-${this.variant}`]: true,
      [`size-${this.size}`]: true,
      [`layout-${layoutName}`]: true,
      'no-top-row': !hasTopRow,
      'no-bottom-row': !hasBottomRow,
      [`font-${typography}`]: true
    })
    const rows = html`
      ${hasTopRow ? this.renderRow('top', layout.top, rowValues) : nothing}
      ${this.renderRow('middle', layout.middle, rowValues)}
      ${hasBottomRow ? this.renderRow('bottom', layout.bottom, rowValues) : nothing}
    `

    return this.as === 'span'
      ? html`<span class=${classes}>${rows}</span>`
      : html`<p class=${classes}>${rows}</p>`
  }

  private resolveTypography(): fontVariantsType[number] {
    if (!this.typography) {
      return 'body'
    }

    return fontVariants.includes(this.typography) ? this.typography : 'body'
  }

  private renderRow(
    rowName: 'top' | 'middle' | 'bottom',
    segments: SegmentToken[],
    values: RowValues,
  ) {
    const content = segments
      .map(segment => this.renderSegment(segment, values))
      .filter(fragment => fragment !== nothing)

    if (content.length === 0) {
      return nothing
    }

    return html`
      <span class="cse-price__row cse-price__row--${rowName}">
        ${content}
      </span>
    `
  }

  private rowHasContent(segments: SegmentToken[], values: RowValues) {
    if (segments.length === 0) {
      return false
    }

    return segments.some(segment => this.segmentHasContent(segment, values))
  }

  private renderSegment(segment: SegmentToken, values: RowValues) {
    switch (segment) {
      case 'lead':
        if (!values.lead) {
          return nothing
        }
        return html`<span class="cse-price__lead">${values.lead}</span>`
      case 'value':
        return html`<span class="cse-price__value font-price-${this.size}"><slot></slot></span>`
      case 'uom':
        if (!values.uom) {
          return nothing
        }
        return html`<span class="cse-price__uom">${values.uom}</span>`
      case 'vat':
        if (!values.vat) {
          return nothing
        }
        return html`<span class="cse-price__vat">${values.vat}</span>`
    }
  }

  private segmentHasContent(segment: SegmentToken, values: RowValues) {
    switch (segment) {
      case 'lead':
        return Boolean(values.lead)
      case 'value':
        return true
      case 'uom':
        return Boolean(values.uom)
      case 'vat':
        return Boolean(values.vat)
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cse-price': CSEPrice
  }
}
